/**
 * Invite links carry the invite secret as `t` (in the fragment, or the query on older
 * builds). The secret alone signs a pending invitee in, so it must never reach Sentry:
 * error events, transactions, breadcrumbs or replay recordings.
 */
const INVITE_PATH_MARKER = '/invitationV2/';
const SECRET_PARAM = /([?#&])t=[^&#\s"'<>]*/g;
const FILTERED = '[Filtered]';

export const scrubInviteSecret = (value: string): string =>
  value.includes(INVITE_PATH_MARKER)
    ? value.replace(SECRET_PARAM, (_match, separator: string) => `${separator}t=${FILTERED}`)
    : value;

/**
 * Only serializable data is walked. Sentry payloads also hold live objects (scopes, the client,
 * DOM nodes) through which `window.location` is reachable; writing its `href` reloads the page
 * and writing `origin` throws (GUA-368).
 *
 * Known gaps, accepted on purpose:
 * - Class instances are skipped everywhere, including the raw `data.arguments` of console
 *   breadcrumbs. Walking them would reach live objects again (`console.log(location)` on the
 *   invite page would navigate). The breadcrumb `message` string is still scrubbed, and no code
 *   logs objects holding invite URLs.
 * - Objects from another realm (an iframe) have a different `Object.prototype`, so they are
 *   skipped too.
 */
const isPlainData = (value: object): boolean => {
  if (Array.isArray(value)) return true;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
};

// Iterative with a seen set: replay snapshots are deep DOM trees, so no depth cap (it would
// silently skip nodes) and no recursion (it could overflow the stack).
const scrubObject = (root: Record<string, unknown>) => {
  const seen = new WeakSet<object>();
  const stack: Record<string, unknown>[] = [root];
  while (stack.length) {
    const record = stack.pop() as Record<string, unknown>;
    if (!seen.has(record)) {
      seen.add(record);
      Object.keys(record).forEach(key => {
        const value = record[key];
        if (typeof value === 'string') {
          const scrubbed = scrubInviteSecret(value);
          if (scrubbed !== value) {
            try {
              record[key] = scrubbed;
            } catch {
              // A read-only property must never stop the event from being sent, so this one
              // value is sent unscrubbed on purpose rather than dropping the whole payload.
            }
          }
        } else if (value && typeof value === 'object' && isPlainData(value)) {
          stack.push(value as Record<string, unknown>);
        }
      });
    }
  }
};

/**
 * Scrubs every string inside a Sentry payload in place and returns it. Payload shapes
 * differ per hook (request.url, breadcrumb data.to/from, span attributes, replay
 * navigation entries), so this walks the whole object instead of listing fields.
 * Takes one argument on purpose: Sentry hooks pass a `hint` second.
 */
export const scrubInviteSecretDeep = <T>(payload: T): T => {
  if (typeof payload === 'string') {
    return scrubInviteSecret(payload) as unknown as T;
  }
  if (payload && typeof payload === 'object') {
    scrubObject(payload as unknown as Record<string, unknown>);
  }
  return payload;
};
